"use client";

import { CircleCheck } from "lucide-react";
import { motion } from "motion/react";
import * as React from "react";
import { cn } from "../../lib";
import {
	CONTENT_TOOLTIP_SHORT,
	ContentTooltip,
	type ContentTooltipSide,
} from "../design-system/tooltip";
const COMPLETE_RING_OUT_MS = 200;
const COMPLETE_CHECK_IN_DELAY_MS = 90;
const COMPLETE_CHECK_IN_MS = 220;
/** Progress arc length + solid/gradient color (e.g. usage band green→orange→red). */
const PROGRESS_STROKE_TRANSITION_MS = 300;

/**
 * Drive arc length with `motion.circle` (Motion owns `strokeDashoffset`).
 *
 * CSS `transition` on SVG dashoffset is unreliable under frequent React commits
 * (jobs NOTIFY). Imperative `animate(domNode, …)` also fails to paint arcs
 * reliably on raw SVG circles — use the declarative `motion.circle` API instead.
 * Bars stay on CSS `transform`, which is fine.
 */
function ProgressMotionCircle({
	cx,
	cy,
	r,
	circumference,
	dashOffset,
	stroke,
	strokeWidth,
	strokeLinecap,
	strokeOpacity,
	transform,
	className,
}: {
	cx: number;
	cy: number;
	r: number;
	circumference: number;
	dashOffset: number;
	stroke: string;
	strokeWidth: number;
	strokeLinecap: "butt" | "round";
	strokeOpacity?: number;
	transform: string;
	className?: string;
}) {
	return (
		<motion.circle
			cx={cx}
			cy={cy}
			r={r}
			fill="none"
			stroke={stroke}
			strokeOpacity={strokeOpacity}
			strokeWidth={strokeWidth}
			strokeLinecap={strokeLinecap}
			strokeDasharray={circumference}
			transform={transform}
			className={className}
			initial={false}
			animate={{ strokeDashoffset: dashOffset }}
			transition={{
				duration: PROGRESS_STROKE_TRANSITION_MS / 1000,
				ease: "easeOut",
			}}
		/>
	);
}

/** Lucide `CircleCheck` geometry (24×24 viewBox). */
const LEARN_COMPLETE_CHECK_STROKE_WIDTH = 2.25;
/** Check mark ring only — background disc stays full `size`. */
const LEARN_COMPLETE_CHECK_SIZE_INSET_PX = 1;

const LEARN_TRACK_FILL_CLASS =
	"fill-[var(--sq-grey-static-from)] dark:fill-[#334155]";
const LEARN_TRACK_STROKE_CLASS =
	"stroke-[var(--sq-grey-static-from)] dark:stroke-[#334155]";

function learnTrackUsesThemeDefault(learnTrackColor?: string): boolean {
	return learnTrackColor == null;
}

interface LearnProgressCompleteCheckIconProps {
	size: number;
	learnTrackColor?: string;
	learnProgressColor: string;
	learnProgressGradientStart?: string;
	learnProgressGradientEnd?: string;
}

/** Learn completion glyph: full-size track disc + smaller centered EXP-green check. */
function LearnProgressCompleteCheckIcon({
	size,
	learnTrackColor,
	learnProgressColor,
	learnProgressGradientStart,
	learnProgressGradientEnd,
}: LearnProgressCompleteCheckIconProps) {
	const gradientId = React.useId();
	const hasGradient = Boolean(
		learnProgressGradientStart && learnProgressGradientEnd,
	);
	const strokePaint = hasGradient
		? `url(#${gradientId})`
		: learnProgressColor;
	const useThemeTrack = learnTrackUsesThemeDefault(learnTrackColor);
	const checkSize = Math.max(0, size - LEARN_COMPLETE_CHECK_SIZE_INSET_PX);

	return (
		<div
			className="relative inline-flex shrink-0"
			style={{ width: size, height: size }}
		>
			{/* biome-ignore lint/a11y/noSvgWithoutTitle: decorative completion disc */}
			<svg
				width={size}
				height={size}
				viewBox="0 0 24 24"
				className="absolute inset-0"
				aria-hidden
				focusable={false}
			>
				<circle
					cx="12"
					cy="12"
					r="12"
					fill={useThemeTrack ? undefined : learnTrackColor}
					className={
						useThemeTrack ? LEARN_TRACK_FILL_CLASS : undefined
					}
				/>
			</svg>
			{/* biome-ignore lint/a11y/noSvgWithoutTitle: decorative completion check */}
			<svg
				width={checkSize}
				height={checkSize}
				viewBox="0 0 24 24"
				className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
				aria-hidden
				focusable={false}
			>
				{hasGradient ? (
					<defs>
						<linearGradient
							id={gradientId}
							x1="0%"
							y1="0%"
							x2="100%"
							y2="100%"
						>
							<stop
								offset="0%"
								style={{
									stopColor: learnProgressGradientStart,
									transition: `stop-color ${PROGRESS_STROKE_TRANSITION_MS}ms ease-out`,
								}}
							/>
							<stop
								offset="100%"
								style={{
									stopColor: learnProgressGradientEnd,
									transition: `stop-color ${PROGRESS_STROKE_TRANSITION_MS}ms ease-out`,
								}}
							/>
						</linearGradient>
					</defs>
				) : null}
				<circle
					cx="12"
					cy="12"
					r="10"
					fill="none"
					stroke={strokePaint}
					strokeWidth={LEARN_COMPLETE_CHECK_STROKE_WIDTH}
				/>
				<path
					d="m9 12 2 2 4-4"
					fill="none"
					stroke={strokePaint}
					strokeWidth={LEARN_COMPLETE_CHECK_STROKE_WIDTH}
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			</svg>
		</div>
	);
}

export interface IntentProgressCircleSegment {
	/** Share of the ring circumference (0–100). Values should sum toward ≤100. */
	value: number;
	color: string;
	gradientEnd?: string;
	/** When true, skip painting (queued gap over track). */
	empty?: boolean;
}

export interface IntentProgressCircleLayer {
	/** Stable key so layer arcs stay mounted while values animate. */
	id?: string;
	/** Share of the ring circumference (0–100). */
	value: number;
	color: string;
	gradientEnd?: string;
	strokeWidth?: number;
	opacity?: number;
}

export interface IntentProgressCircleProps
	extends Omit<React.HTMLAttributes<HTMLDivElement>, "children" | "title"> {
	value?: number | null;
	/**
	 * Multi-color arcs. When set, these replace the single progress stroke.
	 * Empty segments leave the track visible (queued units).
	 */
	segments?: IntentProgressCircleSegment[];
	/** Full-length stacked arcs rendered in array order (later layers on top). */
	layers?: IntentProgressCircleLayer[];
	/**
	 * Outer diameter in px of the widest stroke (track or progress). The stroke
	 * centerline is inset by half that width so the rim fits inside this box.
	 */
	size?: number;
	strokeWidth?: number;
	/** Optional override for the progress stroke width (defaults to `strokeWidth`). */
	progressStrokeWidth?: number;
	isIndeterminate?: boolean;
	/**
	 * When set, applies a learn-specific visual style (thicker donut + thinner progress + patterned
	 * progress stroke). Default undefined keeps existing styling.
	 */
	variant?: "default" | "learn";
	/** Track color when `variant="learn"`. Omit for theme default (grey static `from` light / `#334155` dark). */
	learnTrackColor?: string;
	/** Progress color when `variant="learn"`. */
	learnProgressColor?: string;
	/** Optional gradient start for learn progress stroke. */
	learnProgressGradientStart?: string;
	/** Optional gradient end for learn progress stroke. */
	learnProgressGradientEnd?: string;
	/** Optional diagonal pattern color for learn progress stroke overlay. */
	learnPatternColor?: string;
	/** Optional diagonal pattern opacity for learn progress stroke overlay. */
	learnPatternOpacity?: number;
	/**
	 * When true, wraps the circle in Prefer-over ContentTooltip. Default off.
	 */
	showTooltip?: boolean;
	/** Tooltip body; default is `"{rounded}% complete"` from `value`. */
	tooltipContent?: React.ReactNode;
	tooltipSide?: ContentTooltipSide;
	/**
	 * When true and `value` is 100 (and not indeterminate), the ring scales/fades
	 * out and a check icon scales/fades in. Use `false` for countdown timers.
	 */
	replaceWithCheckWhenComplete?: boolean;
	/**
	 * When false, omits the track ring so only the progress stroke paints.
	 * Default true.
	 */
	showTrack?: boolean;
	/**
	 * When false, omits the learn-variant diagonal pattern overlay.
	 * Default true when `variant="learn"`.
	 */
	showPattern?: boolean;
	/**
	 * End cap of the progress stroke segment.
	 * - `round` (default): rounded segment ends
	 * - `square`: flat/square segment ends
	 */
	segmentCap?: "round" | "square";
	/**
	 * Optional centered integer: `percent` shows rounded `value` (0–100); `timer` shows
	 * ceil seconds from `timerSeconds`.
	 */
	centerMode?: "none" | "percent" | "timer";
	/** Seconds remaining when `centerMode="timer"` (fractional values ceil for display). */
	timerSeconds?: number | null;
	/** Optional classes for the centered percent/timer label. */
	centerLabelClassName?: string;
}

interface ProgressCircleCenterLabelProps {
	mode: "percent" | "timer";
	/** Raw percent (0–100) or seconds for timer mode. */
	value: number;
	/** Outer circle diameter — scales center type for small countdown chips. */
	size: number;
	className?: string;
}

function ProgressCircleCenterLabel({
	mode,
	value,
	size,
	className,
}: ProgressCircleCenterLabelProps) {
	const display =
		mode === "timer"
			? Math.max(0, Math.ceil(Number(value)))
			: Math.round(Math.max(0, Math.min(100, Number(value))));

	return (
		<span
			className="pointer-events-none absolute inset-0 flex items-center justify-center tabular-nums leading-none"
			aria-hidden
		>
			<span
				className={cn(
					"font-black tabular-nums",
					size <= 32 ? "text-[10px]" : "text-[15px]",
					className ?? "text-white text-shadow-md",
				)}
			>
				{display}
			</span>
		</span>
	);
}

export function IntentProgressCircle({
	value = 0,
	segments,
	layers,
	size = 20,
	strokeWidth = 2,
	progressStrokeWidth,
	isIndeterminate = false,
	variant = "default",
	learnTrackColor,
	learnProgressColor = "#84cc16",
	learnProgressGradientStart,
	learnProgressGradientEnd,
	learnPatternColor = "#ffffff",
	learnPatternOpacity = 0.18,
	showTooltip = false,
	tooltipContent,
	tooltipSide = "top",
	replaceWithCheckWhenComplete = false,
	showTrack = true,
	showPattern = true,
	segmentCap = "round",
	centerMode = "none",
	timerSeconds,
	centerLabelClassName,
	className,
	...props
}: IntentProgressCircleProps) {
	const normalizedSegments = (segments ?? [])
		.map((segment) => ({
			...segment,
			value: Math.max(0, Math.min(100, Number(segment.value ?? 0))),
		}))
		.filter((segment) => segment.value > 0);
	const normalizedLayers = (layers ?? []).map((layer) => ({
		...layer,
		value: Math.max(0, Math.min(100, Number(layer.value ?? 0))),
		opacity:
			layer.opacity == null
				? 1
				: Math.max(0, Math.min(1, Number(layer.opacity))),
	}));
	const segmentsValueSum = normalizedSegments.reduce(
		(sum, segment) => sum + segment.value,
		0,
	);
	const hasSegments = normalizedSegments.length > 0;
	/**
	 * Keep zero-value layers mounted. Filtering them out remounts arcs at the
	 * next non-zero % (no CSS transition) — unlike the single-`value` path
	 * used by metered usage, which always keeps one circle in the DOM.
	 */
	const hasLayers = (layers ?? []).length > 0;
	const clampedValue = hasSegments
		? Math.max(0, Math.min(100, segmentsValueSum))
		: hasLayers
			? Math.max(
					0,
					Math.min(
						100,
						normalizedLayers.reduce(
							(max, layer) => Math.max(max, layer.value),
							0,
						),
					),
				)
			: Math.max(0, Math.min(100, Number(value ?? 0)));
	const resolvedProgressStrokeWidth = progressStrokeWidth ?? strokeWidth;
	const progressStrokeLinecap = segmentCap === "square" ? "butt" : "round";
	/**
	 * `size` is the outer diameter of the widest stroke (track or progress).
	 * Centerline sits inset by half that stroke so the rim fits inside size×size
	 * without clipping.
	 */
	const maxLayerStrokeWidth = normalizedLayers.reduce(
		(max, layer) =>
			Math.max(max, layer.strokeWidth ?? resolvedProgressStrokeWidth),
		resolvedProgressStrokeWidth,
	);
	const maxStrokeWidth = Math.max(
		strokeWidth,
		resolvedProgressStrokeWidth,
		maxLayerStrokeWidth,
	);
	const radius = Math.max(0, (size - maxStrokeWidth) / 2);
	const circumference = 2 * Math.PI * radius;
	const offset = circumference - (clampedValue / 100) * circumference;
	const patternId = React.useId();
	const gradientId = React.useId();
	const segmentGradientBaseId = React.useId();

	const isComplete =
		replaceWithCheckWhenComplete &&
		!isIndeterminate &&
		!hasSegments &&
		!hasLayers &&
		clampedValue >= 100;

	const defaultTooltipText = `${Math.round(clampedValue)}% complete`;
	const resolvedTooltip = tooltipContent
		? tooltipContent
		: isComplete && replaceWithCheckWhenComplete
			? "Complete"
			: defaultTooltipText;

	const useThemeLearnTrack = learnTrackUsesThemeDefault(learnTrackColor);

	const progressArcs = hasSegments ? (
		<>
			<defs>
				{normalizedSegments.map((segment, index) => {
					if (segment.empty || !segment.gradientEnd) {
						return null;
					}
					const id = `${segmentGradientBaseId}-${index}`;
					return (
						<linearGradient
							key={id}
							id={id}
							x1="0%"
							y1="0%"
							x2="100%"
							y2="100%"
						>
							<stop offset="0%" stopColor={segment.color} />
							<stop
								offset="100%"
								stopColor={segment.gradientEnd}
							/>
						</linearGradient>
					);
				})}
			</defs>
			{
				normalizedSegments.reduce<{
					nodes: React.ReactNode[];
					cursor: number;
				}>(
					(acc, segment, index) => {
						const length = (segment.value / 100) * circumference;
						const start = acc.cursor;
						acc.cursor += length;
						if (segment.empty) {
							return acc;
						}
						const stroke =
							segment.gradientEnd != null
								? `url(#${segmentGradientBaseId}-${index})`
								: segment.color;
						acc.nodes.push(
							<circle
								key={`seg-${index}`}
								cx={size / 2}
								cy={size / 2}
								r={radius}
								fill="none"
								stroke={stroke}
								strokeWidth={resolvedProgressStrokeWidth}
								strokeLinecap={progressStrokeLinecap}
								strokeDasharray={`${length} ${Math.max(0, circumference - length)}`}
								strokeDashoffset={-start}
								transform={`rotate(-90 ${size / 2} ${size / 2})`}
								className="ease-out transition-[stroke-dashoffset,stroke]"
								style={{
									transitionDuration: `${PROGRESS_STROKE_TRANSITION_MS}ms`,
								}}
							/>,
						);
						return acc;
					},
					{ nodes: [], cursor: 0 },
				).nodes
			}
		</>
	) : hasLayers ? (
		<>
			<defs>
				{normalizedLayers.map((layer, index) => {
					if (!layer.gradientEnd) {
						return null;
					}
					const layerKey = layer.id ?? String(index);
					const gradientIdForLayer = `${segmentGradientBaseId}-layer-${layerKey}`;
					return (
						<linearGradient
							key={gradientIdForLayer}
							id={gradientIdForLayer}
							x1="0%"
							y1="0%"
							x2="100%"
							y2="100%"
						>
							<stop
								offset="0%"
								stopColor={layer.color}
								style={{
									transition: `stop-color ${PROGRESS_STROKE_TRANSITION_MS}ms ease-out`,
								}}
							/>
							<stop
								offset="100%"
								stopColor={layer.gradientEnd}
								style={{
									transition: `stop-color ${PROGRESS_STROKE_TRANSITION_MS}ms ease-out`,
								}}
							/>
						</linearGradient>
					);
				})}
			</defs>
			{normalizedLayers.map((layer, index) => {
				const layerKey = layer.id ?? String(index);
				const layerStrokeWidth =
					layer.strokeWidth ?? resolvedProgressStrokeWidth;
				const gradientIdForLayer = `${segmentGradientBaseId}-layer-${layerKey}`;
				const stroke =
					layer.gradientEnd != null
						? `url(#${gradientIdForLayer})`
						: layer.color;
				const layerOffset =
					circumference - (layer.value / 100) * circumference;
				return (
					<ProgressMotionCircle
						key={layerKey}
						cx={size / 2}
						cy={size / 2}
						r={radius}
						circumference={circumference}
						dashOffset={layerOffset}
						stroke={stroke}
						strokeOpacity={layer.opacity}
						strokeWidth={layerStrokeWidth}
						strokeLinecap={progressStrokeLinecap}
						transform={`rotate(-90 ${size / 2} ${size / 2})`}
					/>
				);
			})}
		</>
	) : (
		<ProgressMotionCircle
			cx={size / 2}
			cy={size / 2}
			r={radius}
			circumference={circumference}
			dashOffset={isIndeterminate ? circumference * 0.4 : offset}
			stroke={
				variant === "learn"
					? learnProgressGradientStart && learnProgressGradientEnd
						? `url(#${gradientId})`
						: learnProgressColor
					: "currentColor"
			}
			strokeWidth={resolvedProgressStrokeWidth}
			strokeLinecap={progressStrokeLinecap}
			transform={`rotate(-90 ${size / 2} ${size / 2})`}
			className={variant === "learn" ? undefined : "text-primary"}
		/>
	);

	const ringSvg = (
		/* biome-ignore lint/a11y/noSvgWithoutTitle: decorative; parent has role="progressbar" + aria-valuenow */
		<svg
			width={size}
			height={size}
			viewBox={`0 0 ${size} ${size}`}
			className={cn(
				isIndeterminate && !hasSegments && !hasLayers && "animate-spin",
			)}
			aria-hidden
			focusable={false}
		>
			{showTrack ? (
				<circle
					cx={size / 2}
					cy={size / 2}
					r={radius}
					fill="none"
					stroke={
						variant === "learn"
							? useThemeLearnTrack
								? undefined
								: learnTrackColor
							: "currentColor"
					}
					className={cn(
						variant === "learn" && useThemeLearnTrack
							? LEARN_TRACK_STROKE_CLASS
							: undefined,
					)}
					strokeOpacity={variant === "learn" ? 1 : 0.2}
					strokeWidth={strokeWidth}
				/>
			) : null}
			{progressArcs}
			{variant === "learn" && !hasSegments && !hasLayers ? (
				<>
					<defs>
						{learnProgressGradientStart &&
						learnProgressGradientEnd ? (
							<linearGradient
								id={gradientId}
								x1="0%"
								y1="0%"
								x2="100%"
								y2="100%"
							>
								<stop
									offset="0%"
									style={{
										stopColor: learnProgressGradientStart,
										transition: `stop-color ${PROGRESS_STROKE_TRANSITION_MS}ms ease-out`,
									}}
								/>
								<stop
									offset="100%"
									style={{
										stopColor: learnProgressGradientEnd,
										transition: `stop-color ${PROGRESS_STROKE_TRANSITION_MS}ms ease-out`,
									}}
								/>
							</linearGradient>
						) : null}
						{showPattern ? (
							<pattern
								id={patternId}
								patternUnits="userSpaceOnUse"
								width="8"
								height="8"
								patternTransform="rotate(-45)"
							>
								<rect width="8" height="8" fill="transparent" />
								<rect
									x="0"
									y="0"
									width="3"
									height="8"
									fill={learnPatternColor}
									opacity={learnPatternOpacity}
								/>
							</pattern>
						) : null}
					</defs>
					{showPattern ? (
						<circle
							cx={size / 2}
							cy={size / 2}
							r={radius}
							fill="none"
							stroke={`url(#${patternId})`}
							strokeWidth={resolvedProgressStrokeWidth}
							strokeLinecap={progressStrokeLinecap}
							strokeDasharray={circumference}
							strokeDashoffset={
								isIndeterminate ? circumference * 0.4 : offset
							}
							transform={`rotate(-90 ${size / 2} ${size / 2})`}
							style={{ pointerEvents: "none" }}
						/>
					) : null}
				</>
			) : null}
		</svg>
	);

	const showCenterLabel =
		centerMode !== "none" &&
		!isIndeterminate &&
		!(replaceWithCheckWhenComplete && isComplete);

	const ringContent = (
		<div
			className="relative inline-flex"
			style={{ width: size, height: size }}
		>
			{ringSvg}
			{showCenterLabel ? (
				<ProgressCircleCenterLabel
					mode={centerMode === "percent" ? "percent" : "timer"}
					size={size}
					className={centerLabelClassName}
					value={
						centerMode === "percent"
							? clampedValue
							: Number(timerSeconds ?? 0)
					}
				/>
			) : null}
		</div>
	);

	const circle = replaceWithCheckWhenComplete ? (
		<div
			className={cn(
				"relative inline-flex items-center justify-center",
				showTooltip && "cursor-default",
				className,
			)}
			style={{ width: size, height: size }}
			role="progressbar"
			aria-valuemin={0}
			aria-valuemax={100}
			aria-valuenow={
				isIndeterminate ? undefined : isComplete ? 100 : clampedValue
			}
			aria-label={
				props["aria-label"] ?? (isComplete ? "Complete" : "Progress")
			}
			{...props}
		>
			<div
				className={cn(
					"absolute inset-0 flex origin-center items-center justify-center transition-[transform,opacity] ease-out",
					isComplete ? "scale-0 opacity-0" : "scale-100 opacity-100",
				)}
				style={{
					transitionDuration: `${COMPLETE_RING_OUT_MS}ms`,
				}}
			>
				{ringContent}
			</div>
			<div
				className={cn(
					"absolute inset-0 flex origin-center items-center justify-center transition-[transform,opacity] ease-out",
					isComplete
						? "scale-100 opacity-100"
						: "pointer-events-none scale-0 opacity-0",
				)}
				style={{
					transitionDuration: `${COMPLETE_CHECK_IN_MS}ms`,
					transitionDelay: isComplete
						? `${COMPLETE_CHECK_IN_DELAY_MS}ms`
						: "0ms",
				}}
				aria-hidden
			>
				{variant === "learn" ? (
					<LearnProgressCompleteCheckIcon
						size={size}
						learnTrackColor={learnTrackColor}
						learnProgressColor={learnProgressColor}
						learnProgressGradientStart={learnProgressGradientStart}
						learnProgressGradientEnd={learnProgressGradientEnd}
					/>
				) : (
					<CircleCheck
						className="text-primary"
						strokeWidth={LEARN_COMPLETE_CHECK_STROKE_WIDTH}
						style={{
							width: Math.max(
								0,
								size - LEARN_COMPLETE_CHECK_SIZE_INSET_PX,
							),
							height: Math.max(
								0,
								size - LEARN_COMPLETE_CHECK_SIZE_INSET_PX,
							),
						}}
						aria-hidden
						focusable={false}
					/>
				)}
			</div>
		</div>
	) : (
		<div
			className={cn(
				"inline-flex items-center justify-center",
				showTooltip && "cursor-default",
				className,
			)}
			style={{ width: size, height: size }}
			role="progressbar"
			aria-valuemin={0}
			aria-valuemax={100}
			aria-valuenow={isIndeterminate ? undefined : clampedValue}
			aria-label={props["aria-label"] ?? "Progress"}
			{...props}
		>
			{ringContent}
		</div>
	);

	if (!showTooltip) {
		return circle;
	}

	return (
		<ContentTooltip
			{...CONTENT_TOOLTIP_SHORT}
			side={tooltipSide}
			content={resolvedTooltip}
			triggerClassName="inline-flex"
		>
			{circle}
		</ContentTooltip>
	);
}
